import React from 'react';

interface InfoRow {
    label: string;
    value: string;
    description: string;
}

interface InfoTableProps {
    title: string;
    data: InfoRow[];
}

const InfoTable: React.FC<InfoTableProps> = ({ title, data }) => (
    <div className="bg-white border border-gray-200 rounded-2xl shadow-sm p-4 sm:p-8">
        <h2 className="text-xl font-semibold text-slate-900 mb-6">{title}</h2>
        <div className="overflow-x-auto">
            <table className="w-full">
                <thead>
                    <tr className="border-b border-gray-200">
                        <th className="text-left py-3 px-4 text-sm font-medium text-slate-600">Term</th>
                        <th className="text-left py-3 px-4 text-sm font-medium text-slate-600">Formula / Value</th>
                        <th className="text-left py-3 px-4 text-sm font-medium text-slate-600">Explanation</th>
                    </tr>
                </thead>
                <tbody>
                    {data.map((row, index) => (
                        <tr key={index} className="border-b border-gray-100">
                            <td className="py-3.5 px-4 text-slate-900 font-medium">
                                {row.label}
                            </td>
                            <td className="py-3.5 px-4 text-slate-700 font-mono text-sm">
                                {row.value}
                            </td>
                            <td className="py-3.5 px-4 text-slate-600 text-sm">
                                {row.description}
                            </td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    </div>
);

export default function NRRCalculation(): React.ReactElement {
    const nrrFormulaData: InfoRow[] = [
        {
            label: 'Run Rate (Scored)',
            value: 'Total Runs Scored / Total Overs Faced',
            description: 'Average runs scored per over by the team',
        },
        {
            label: 'Run Rate (Conceded)',
            value: 'Total Runs Conceded / Total Overs Bowled',
            description: 'Average runs conceded per over by the team',
        },
        {
            label: 'Net Run Rate (NRR)',
            value: 'Run Rate (Scored) − Run Rate (Conceded)',
            description: 'Difference between scoring rate and conceding rate',
        },
    ];

    const nrrNotesData: InfoRow[] = [
        {
            label: 'All Matches Count',
            value: 'Cumulative',
            description: 'NRR is calculated using totals from all matches played',
        },
        {
            label: 'All-Out Scenario',
            value: 'Full Overs Counted',
            description: 'If a team is all out early, full quota of overs is still used',
        },
        {
            label: 'Higher NRR',
            value: 'Better Ranking',
            description: 'Teams with higher NRR are ranked higher when points are equal',
        },
    ];

    return (
        <div className="min-h-screen py-6 px-4">
            <div className="max-w-6xl mx-auto">
                {/* Header Section */}
                <div className="bg-white border border-gray-200 rounded-2xl shadow-sm p-4 sm:p-8 mb-6">
                    <h1 className="text-slate-900 text-3xl font-semibold mb-3">
                        Net Run Rate (NRR) Calculation
                    </h1>
                    <p className="text-slate-600 leading-relaxed">
                        Net Run Rate (NRR) is a statistical method used in cricket tournaments
                        to rank teams with equal points. It measures how efficiently a team
                        scores runs compared to how efficiently it concedes runs.
                    </p>
                </div>

                {/* Formula Highlight */}
                <div className="bg-slate-50 border border-gray-200 rounded-2xl shadow-sm p-4 sm:p-8 mb-6 flex justify-center">
                    <img src="/assets/images/net-run-rate.png" alt="Net Run Rate" />
                </div>

                {/* Tables */}
                <div className="space-y-6">
                    <InfoTable
                        title="NRR Formula Breakdown"
                        data={nrrFormulaData}
                    />
                    <InfoTable
                        title="Important NRR Rules & Notes"
                        data={nrrNotesData}
                    />
                </div>
            </div>
        </div>
    );
}
